<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <b-form-group class="col-lg-12 col-md-12 col-sm-12 mb-2">
            <b-form-group label="VIP limits mode" class="mb-0">
              <b-form-radio-group
                v-model="vipMode"
                buttons
                :options="[
                  { value: 'byTable', text: 'by Table' },
                  { value: 'byPlayer', text: 'by Player' }
                ]"
              />
            </b-form-group>
          </b-form-group>
        </b-row>
      </div>
    </section>

    <section v-show="vipMode === 'byTable'" class="content">
      <div class="container-fluid">
        <b-row class="col-lg-12">
          <b-form-group class="col-lg-12 col-md-12 col-sm-12">
            <div class="d-flex flex-wrap align-items-center">
                <EditForm
                  :defaultValues="{}"
                  create
                  title="Create limit"
                  @finish="handleItemsUpdate"
                >
                  <template #button="{ onclick }">
                    <b-button
                      type="button"
                      class="mr-1"
                      variant="success"
                      @click="onclick"
                      ><i class="fas fa-plus" /> Create</b-button
                    >
                  </template>
                </EditForm>

                <b-button
                  type="button"
                  class="mr-1"
                  variant="primary"
                  @click="
                    () => {
                      this.copyModalOpened = true
                    }
                  "
                  ><i class="fas fa-copy" />&nbsp;Copy limits</b-button
                >

                <b-button
                  type="button"
                  class="mr-1"
                  @click="
                    () => {
                      this.importModalOpened = true
                    }
                  "
                  ><i class="fas fa-file-import" />&nbsp;Import / Export</b-button
                >
            </div>

            <b-button
              v-if="deleteButton.show && deleteButton.deleteCount > 0"
              type="button"
              class="mr-1"
              variant="danger"
              @click="
                () => {
                  this.deleteButton.confirmModal = true
                }
              "
              ><i class="fas fa-trash" />&nbsp;Remove All ({{
                deleteButton.deleteCount
              }})</b-button
            >
            <b-modal
              ref="modal-container"
              v-model="deleteButton.confirmModal"
              size="lg"
              title="Confirm remove?"
              hide-footer
            >
              <span v-if="isAllOperatorsSelected()">
                Please confirm removing limits for
                <b>all {{ getFilteredOperators().length }} operators</b> (total:
                <b>{{ deleteButton.deleteCount }}</b> limits).
              </span>
              <span v-else>
                Please confirm removing:
                <b>{{ deleteButton.deleteCount }} limits</b>.
              </span>
              <b-button
                class="mt-2 float-right"
                variant="danger"
                @click="deleteLimits"
                >Remove</b-button
              >
            </b-modal>
          </b-form-group>
        </b-row>

        <LimitRecalculation
          :selected-ids="selectedLimitIds"
          @restore-selection="selectedLimitIds = $event"
          @busy="recalculationBusy = $event"
          @completed="loadItems"
        />

        <b-row class="col-lg-12">
          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-group label="Game Type:" label-for="filter-type">
              <div class="d-flex">
                <multiselect
                  id="filter-type"
                  v-model="filters.type"
                  :options="this.$store.getters['limits/typesForSelect'] || []"
                  :custom-label="
                    opt =>
                      this.$store.getters['isShowDebugTools']
                        ? opt.value
                        : opt.label
                  "
                  track-by="value"
                  :multiple="false"
                  :placeholder="$t('Select')"
                  :select-label="$t('Select')"
                  :deselect-label="$t('Remove')"
                  :selected-label="$t('Selected')"
                  :disabled="
                    this.$store.getters['limits/typesForSelect'] === null
                  "
                  :clear-on-select="false"
                  :close-on-select="true"
                  :show-labels="true"
                  :allow-empty="true"
                  :clearable="true"
                  :preserve-search="true"
                  class="flex-grow-1"
                />
                <b-button
                  v-if="filters.type"
                  variant="light"
                  size="sm"
                  class="ml-1 clear-btn"
                  @click="filters.type = null"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </b-form-group>
          </div>

          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-group label="Partner:" label-for="filter-partner">
              <div class="d-flex">
                <multiselect
                  id="filter-partner"
                  v-model="filters.partnerGroupId"
                  :options="partnerOptions"
                  :custom-label="
                    opt =>
                      this.$store.getters['isShowDebugTools']
                        ? opt.value
                        : opt.text
                  "
                  track-by="value"
                  :multiple="false"
                  :placeholder="$t('Select')"
                  :select-label="$t('Select')"
                  :deselect-label="$t('Remove')"
                  :selected-label="$t('Selected')"
                  :disabled="!this.$store.getters['partnersForSelect/loaded']"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :show-labels="true"
                  :allow-empty="true"
                  :clearable="true"
                  :preserve-search="true"
                  class="flex-grow-1"
                  @input="onPartnerChange"
                />
                <b-button
                  v-if="filters.partnerGroupId"
                  variant="light"
                  size="sm"
                  class="ml-1 clear-btn"
                  @click="clearPartner"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </b-form-group>
          </div>

          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-group
              :label="`Operator: (${getFilteredOperators().length})`"
              label-for="filter-operator"
            >
              <div class="d-flex">
                <multiselect
                  id="filter-operator"
                  v-model="filters.partnerId"
                  :options="getOperatorsWithAll()"
                  :custom-label="
                    opt =>
                      opt.isAll
                        ? `All operators (${getFilteredOperators().length})`
                        : this.$store.getters['isShowDebugTools']
                        ? opt.value
                        : opt.label
                  "
                  track-by="value"
                  :multiple="false"
                  :placeholder="$t('Select')"
                  :select-label="$t('Select')"
                  :deselect-label="$t('Remove')"
                  :selected-label="$t('Selected')"
                  :disabled="
                    this.$store.getters['limits/operatorsForSelect'] === null
                  "
                  :clear-on-select="false"
                  :close-on-select="true"
                  :show-labels="true"
                  :allow-empty="!this.filters.partnerGroupId"
                  :clearable="!this.filters.partnerGroupId"
                  :preserve-search="true"
                  class="flex-grow-1"
                />
                <b-button
                  v-if="filters.partnerId && !this.filters.partnerGroupId"
                  variant="light"
                  size="sm"
                  class="ml-1 clear-btn"
                  @click="filters.partnerId = null"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </b-form-group>
          </div>

          <div class="col-lg-3 col-md-3 col-sm-12">
            <b-form-group label="Table:" label-for="filter-tableId">
              <div class="d-flex">
                <multiselect
                  id="filter-tableId"
                  v-model="filters.tableId"
                  :options="getTablesOptions()"
                  :custom-label="
                    opt =>
                      this.$store.getters['isShowDebugTools']
                        ? opt.value
                        : opt.value + ' - ' + opt.label
                  "
                  track-by="value"
                  :multiple="false"
                  :placeholder="$t('Select')"
                  :select-label="$t('Select')"
                  :deselect-label="$t('Remove')"
                  :selected-label="$t('Selected')"
                  :disabled="
                    this.$store.getters['tablesForSelect/list'] === null
                  "
                  :clear-on-select="false"
                  :close-on-select="true"
                  :show-labels="true"
                  :allow-empty="true"
                  :clearable="true"
                  :preserve-search="true"
                  class="flex-grow-1"
                />
                <b-button
                  v-if="filters.tableId"
                  variant="light"
                  size="sm"
                  class="ml-1 clear-btn"
                  @click="filters.tableId = null"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </b-form-group>
          </div>

          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-group label="Currency:" label-for="filter-currency">
              <multiselect
                id="filter-currency"
                v-model="filters.currency"
                :options="
                  this.$store.getters['limits/searchCurrenciesForSelect'](
                    currenciesQuery
                  ) || []
                "
                :custom-label="
                  opt =>
                    this.$store.getters['isShowDebugTools']
                      ? opt.value
                      : opt.label
                "
                track-by="value"
                :multiple="false"
                :placeholder="$t('Select')"
                :select-label="$t('Select')"
                :deselect-label="$t('Remove')"
                :selected-label="$t('Selected')"
                :disabled="
                  this.$store.getters['limits/currenciesForSelect'] === null
                "
                :internal-search="false"
                @search-change="query => (currenciesQuery = query)"
                :clear-on-select="false"
                :close-on-select="true"
                :show-labels="true"
                :allow-empty="true"
                :clearable="true"
                :preserve-search="true"
              />
            </b-form-group>
          </div>

          <div class="col-lg-2 col-md-2 col-sm-12">
            <b-form-group label="Limit Type:" label-for="filter-limitType">
              <div class="d-flex">
                <multiselect
                  id="filter-limitType"
                  v-model="filters.limitType"
                  :options="limitTypeFilterOptions"
                  label="text"
                  track-by="value"
                  :multiple="false"
                  :placeholder="$t('Select')"
                  :select-label="$t('Select')"
                  :deselect-label="$t('Remove')"
                  :selected-label="$t('Selected')"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :show-labels="true"
                  :allow-empty="true"
                  :clearable="true"
                  :preserve-search="true"
                  class="flex-grow-1"
                />
                <b-button
                  v-if="filters.limitType"
                  variant="light"
                  size="sm"
                  class="ml-1 clear-btn"
                  @click="filters.limitType = null"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <filter-buttons
              :cancel="this.cancel"
              :loadFn="loadItems"
              :cancelFn="cancelRequest"
              :resetFn="clearFilters"
            />
          </div>
        </b-row>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="table-wrapper mb-10">
                <b-table
                  :striped="true"
                  :small="true"
                  :hover="true"
                  :empty-text="emptyText"
                  show-empty
                  :sort-by="sortBy"
                  :sort-desc="sortDesc"
                  :busy="!!cancel"
                  :items="items"
                  :fields="fields"
                  :no-local-sorting="true"
                  :sort-icon-left="true"
                  @sort-changed="sortingChanged"
                >
                  <template #head(selected)>
                    <b-form-checkbox :checked="allPageSelected" :indeterminate="somePageSelected && !allPageSelected"
                      :disabled="recalculationBusy || !items.length || !!cancel" aria-label="Select this page"
                      @change="selectPage" />
                  </template>
                  <template #cell(selected)="row">
                    <b-form-checkbox v-model="selectedLimitIds" :value="row.item.id"
                      :disabled="recalculationBusy"
                      :aria-label="'Select limit ' + row.item.tableId + ' ' + row.item.currency + ' ' + row.item.id" />
                  </template>
                  <template #cell(type)="row">
                    <span v-if="row.item.type">
                      {{ $store.getters['limits/getTypeName'](row.item.type) }}
                    </span>
                    <span v-else>-</span>
                  </template>
                  <template #cell(operator)="row">
                    <OperatorLabel :value="row.item.partnerId" />
                  </template>
                  <template #cell(limitType)="row">
                    <span
                      class="badge"
                      :class="
                        isDefaultLimitType(row.item.limitType)
                          ? 'badge-primary'
                          : 'badge-success'
                      "
                    >
                      {{ getLimitTypeDisplay(row.item) }}
                    </span>
                  </template>
                  <template #cell(actions)="row">
                    <EditForm
                      :defaultValues="{ ...row.item }"
                      title="Edit limit"
                      @finish="handleItemsUpdate"
                    />
                    <RemoveForm
                      :object="row.item.id"
                      message="Please confirm removing limit"
                      :url="'/api/v1/limits/' + row.item.id"
                      @finish="handleItemsUpdate"
                    />
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <b-pagination
                  class="float-right"
                  v-model="pagination.page"
                  :total-rows="pagination.records"
                  :per-page="pagination.onPage"
                  @change="onPageChange"
                  first-number
                  last-number
                />
              </div>
            </div>

            <div class="row mt-10 mb-10">
              <div class="col-12">
                <ve-pagination
                  class="float-right"
                  :page-index="pagination.page"
                  :page-size="pagination.onPage"
                  :total="pagination.records"
                  :pagingCount="pagination.count"
                  :layout="['total', 'sizer']"
                  :pageSizeOption="pagination.pageSizeOption"
                  @on-page-size-change="onPageSizeChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CopyLimits
        v-if="copyModalOpened"
        @finish="handleItemsUpdate"
        @close="
          () => {
            this.copyModalOpened = false
          }
        "
      />

      <ImportExportLimits
        v-if="importModalOpened"
        @finish="handleItemsUpdate"
        @close="
          () => {
            this.importModalOpened = false
          }
        "
      />
    </section>
    <PlayerVipLimits v-if="vipMode === 'byPlayer'" />
  </div>
</template>

<script>
import EditLimitForm from '../../components/Limits/EditLimitForm.vue'
import TableName from '../../components/Limits/TableName.vue'
import CopyLimits from '../../components/Limits/CopyLimits.vue'
import ImportExportLimits from '../../components/Limits/ImportExportLimits.vue'
import GameTypeLabel from '../../components/GameTypeLabel.vue'
import OperatorLabel from '../../components/Limits/OperatorLabel.vue'
import RemoveForm from '../../components/remove-form/RemoveForm.vue'
import axios from 'axios'
import { getRouteIcon, ROUTE_LIMITS } from '../../utils/routerIcons'
import Multiselect from 'vue-multiselect'
import FilterButtons from '../../components/FilterButtons'
import {
  LIMIT_TYPES,
  getLimitTypeText,
  isDefaultLimitType as checkIsDefaultLimitType
} from '../../constants/LimitTypes'
import PlayerVipLimits from './PlayerVipLimits.vue'
import LimitRecalculation from '../../components/Limits/LimitRecalculation.vue'

function getDefaultPagination() {
  return {
    onPage: 10,
    page: 1,
    records: 0,
    count: 0,
    pageSizeOption: [10, 25, 50, 100, 200]
  }
}

function getDefaultFilters() {
  return {
    type: null,
    partnerId: null,
    partnerGroupId: null,
    tableId: null,
    currency: null,
    limitType: null
  }
}

export default {
  components: {
    EditForm: EditLimitForm,
    GameTypeLabel,
    OperatorLabel,
    RemoveForm,
    Multiselect,
    FilterButtons,
    TableName,
    CopyLimits,
    ImportExportLimits,
    PlayerVipLimits,
    LimitRecalculation
  },
  data() {
    return {
      currenciesQuery: '',
      cancel: null,
      error: null,
      vipMode: 'byTable',
      selectedLimitIds: [],
      recalculationBusy: false,
      filters: getDefaultFilters(),
      fields: [
        { key: 'selected', label: '', thStyle: { width: '40px' } },
        { key: 'type', label: 'Game Type' },
        { key: 'operator', label: 'Operator', sortable: false },
        { key: 'tableId', label: 'Table ID', sortable: true },
        { key: 'currency', label: 'Currency', sortable: true },
        { key: 'limitType', label: 'Limit Type', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      items: [],
      sortBy: null,
      sortDesc: null,
      pagination: getDefaultPagination(),
      copyModalOpened: false,
      importModalOpened: false,
      deleteButton: {
        confirmModal: false,
        show: false,
        filters: {},
        deleteCount: 0
      }
    }
  },
  watch: {
    typeFilter() {
      if (
        this.getTablesOptions()
          .map(table => table.value)
          .includes(this.filters.tableId)
      ) {
        return
      }
      this.filters.tableId = null
    }
  },
  computed: {
    allPageSelected() {
      return this.items.length > 0 && this.items.every(item => this.selectedLimitIds.includes(item.id))
    },
    somePageSelected() {
      return this.items.some(item => this.selectedLimitIds.includes(item.id))
    },
    emptyText() {
      if (this.cancel) {
        return 'Loading items..'
      }

      if (this.error) {
        return this.error
      }

      return 'There are no records to show'
    },
    typeFilter() {
      return this.filters.type
    },
    routeIcon: () => getRouteIcon(ROUTE_LIMITS),
    partnerOptions() {
      const options = [{ value: null, text: '' }]

      if (this.$store.getters['partnersForSelect/list']) {
        this.$store.getters[
          'partnersForSelect/list'
        ].forEach(({ value, label }) => options.push({ value, text: label }))
      }

      return options
    },
    limitTypeFilterOptions() {
      return LIMIT_TYPES
    }
  },
  methods: {
    selectPage(checked) {
      const pageIds = this.items.map(item => item.id)
      this.selectedLimitIds = checked
        ? [...new Set([...this.selectedLimitIds, ...pageIds])]
        : this.selectedLimitIds.filter(id => !pageIds.includes(id))
    },
    getTablesOptions() {
      let tables = this.$store.getters['tablesForSelect/list'] || []

      return tables.filter(table => {
        if (!this.filters.type) {
          return true
        }

        return parseInt(this.filters.type.value) === table.gameType
      })
    },
    deleteLimits() {
      this.deleteButton.confirmModal = false

      if (this.isAllOperatorsSelected()) {
        this.deleteAllOperatorsLimits()
      } else {
        this.deleteSingleOperatorLimits()
      }
    },
    deleteSingleOperatorLimits() {
      this.$http({
        url: '/api/v1/limits/delete-all',
        data: {
          ...this.deleteButton.filters
        },
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(() => {
          this.loadItems()
        })
        .catch(err => {
          this.error = err
        })
        .finally(() => {
          this.cancel = null
        })
    },
    deleteAllOperatorsLimits() {
      const operators = this.getFilteredOperators()
      const totalOperators = operators.length
      let processedOperators = 0
      let errors = []

      this.cancel = () => {}

      const deleteNextOperator = index => {
        if (index >= operators.length) {
          if (errors.length > 0) {
            this.error = `Errors occurred while deleting limits for ${errors.length} operators`
            console.error('Delete errors:', errors)
          }
          this.loadItems()
          return
        }

        const operator = operators[index]
        const params = {
          ...this.deleteButton.filters,
          partnerId: operator.value
        }

        if (params.emptyOperators) {
          delete params.emptyOperators
        }

        if (params.partnerGroupId) {
          delete params.partnerGroupId
        }

        this.$http({
          url: '/api/v1/limits/delete-all',
          data: params,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(() => {
            processedOperators++
            console.log(
              `Deleted limits for operator ${operator.value} (${processedOperators}/${totalOperators})`
            )
            deleteNextOperator(index + 1)
          })
          .catch(err => {
            processedOperators++
            errors.push({ operator: operator.value, error: err })
            console.error(
              `Error deleting limits for operator ${operator.value}:`,
              err
            )
            deleteNextOperator(index + 1)
          })
      }

      deleteNextOperator(0)
    },
    clearFilters() {
      this.filters = getDefaultFilters()
      this.sortingChanged({
        sortBy: null,
        sortDesc: null
      })
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.pagination.page = 1

      this.loadItems()
    },
    handleItemsUpdate() {
      this.loadItems()
    },
    loadItems: function() {
      this.items = []

      let self = this
      const params = {
        limit: this.pagination.onPage,
        offset: this.pagination.onPage * (this.pagination.page - 1),
        direction: this.sortDesc ? 'DESC' : 'ASC'
      }

      if (this.filters.type?.value) {
        params.type = this.filters.type.value
      }

      if (this.filters.tableId?.value) {
        params.tableId = this.filters.tableId.value
      }

      if (this.filters.currency?.value) {
        params.currency = this.filters.currency.value
      }

      if (this.filters.limitType?.value !== undefined) {
        params.limitType = this.filters.limitType.value
      }

      if (this.sortBy) {
        params.orderBy = this.sortBy
      }

      if (this.filters.partnerId) {
        if (this.filters.partnerId.value === 'all') {
          const operatorIds = this.getFilteredOperators().map(op => op.value)

          if (operatorIds.length === 0) {
            params.emptyOperators = true
          } else if (
            this.filters.partnerGroupId &&
            this.filters.partnerGroupId.value
          ) {
            params.partnerGroupId = this.filters.partnerGroupId.value
          } else {
            params.partnerId = operatorIds
          }
        } else {
          params.partnerId = this.filters.partnerId.value
        }
      }

      if (params.partnerId || params.partnerGroupId || params.emptyOperators) {
        this.deleteButton.filters = params
        this.deleteButton.show = false
      } else {
        this.deleteButton.filters = {}
        this.deleteButton.show = false
      }

      console.log('Request params:', params)

      this.$http({
        url: '/api/v1/limits',
        params: params,
        method: 'GET',
        paramsSerializer: params => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return value.map(v => `${key}[]=${v}`).join('&')
              }
              return `${key}=${value}`
            })
            .join('&')
        },
        cancelToken: new axios.CancelToken(c => {
          self.cancel = c
        })
      })
        .then(resp => {
          let {
            items,
            pagination: { limit, totalCount }
          } = resp.data

          this.items = items
          this.pagination.onPage = limit
          this.pagination.records = totalCount
          if (
            this.deleteButton.filters.partnerId ||
            this.deleteButton.filters.partnerGroupId ||
            this.deleteButton.filters.emptyOperators
          ) {
            this.deleteButton.show = true
            this.deleteButton.deleteCount = totalCount
          }
        })
        .catch(err => {
          this.items = []
          this.error = err
        })
        .finally(() => {
          this.cancel = null
        })
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request')
      }
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1
      this.pagination.onPage = pageSize
      this.loadItems()
    },
    onFinishEdit() {
      this.loadItems()
    },
    getFilteredOperators() {
      const operators = this.$store.getters['limits/operatorsForSelect'] || []

      if (!this.filters.partnerGroupId || !this.filters.partnerGroupId.value) {
        return operators
      }

      return operators.filter(
        operator => operator.partnerId === this.filters.partnerGroupId.value
      )
    },
    getOperatorsWithAll() {
      const operators = this.getFilteredOperators()

      if (this.filters.partnerGroupId) {
        return [
          {
            value: 'all',
            label: `All operators (${operators.length})`,
            isAll: true
          },
          ...operators
        ]
      }

      return operators
    },
    onPartnerChange(partner) {
      if (partner) {
        this.$nextTick(() => {
          this.filters.partnerId = {
            value: 'all',
            label: `All operators (${this.getFilteredOperators().length})`,
            isAll: true
          }
        })
      } else {
        this.filters.partnerId = null
      }
    },
    clearPartner() {
      this.filters.partnerGroupId = null
      this.filters.partnerId = null
    },
    isAllOperatorsSelected() {
      return this.filters.partnerId && this.filters.partnerId.value === 'all'
    },
        getLimitTypeDisplay(item) {
      // Используем константы для отображения
      if (checkIsDefaultLimitType(item.limitType)) {
        return item.limitName || 'Default'
      }

      return item.limitName || getLimitTypeText(item.limitType)
    },
    isDefaultLimitType(limitType) {
      return checkIsDefaultLimitType(limitType)
    }
  },
  async beforeMount() {
    await this.$store.dispatch('partnersForSelect/loadList')
    await this.$store.dispatch('limits/ensureLoadTypesForSelect')
  },
  mounted() {
    if (this.vipMode === 'byTable') {
      this.loadItems()
    }
  }
}
</script>
